import React, {useEffect, useRef, useCallback, useState, useMemo} from 'react';
import { debounce } from 'lodash';
import {extractPageRange} from '../services/pdfUtils';

const pdfPageMetaDefaultValues = {
    pageCount: 1,
    startPage: 1,
    endPage: 1,
};

export default function useNavigatePdfPages({ pageCount, originalBlobUrl, initialStartPage = 1, initialEndPage = 1 }) {
    const [pdfPageMeta, setPdfPageMeta] = useState({
        pageCount,
        startPage: initialStartPage,
        endPage: initialEndPage,
    });
    const [blobUrl, setBlobUrl] = useState(originalBlobUrl || null);
    const [processing, setProcessing] = useState(false);
    const currentBlobRef = useRef(originalBlobUrl || null);

    // Actual extractPages function
    const extractPagesFn = useCallback(async (start, end) => {
        if (!originalBlobUrl) {
            return null;
        }

        if (start === 1 && end === pageCount) {
            return originalBlobUrl;
        }

        const blob = await extractPageRange(originalBlobUrl, start, end);

        return URL.createObjectURL(blob);

    }, [originalBlobUrl, pageCount]);

    // Debounced version of extractPages
    const debouncedExtractPages = useMemo(() => {
        return debounce(async (start, end, callback) => {
            try {
                setProcessing(true);

                const newBlobUrl = await extractPagesFn(start, end);

                // Revoke previous blob if needed
                if (currentBlobRef.current && currentBlobRef.current !== originalBlobUrl) {
                    URL.revokeObjectURL(currentBlobRef.current);
                }

                currentBlobRef.current = newBlobUrl;

                callback(newBlobUrl);
            } finally {
                setProcessing(false);
            }
        }, 300);
    }, [extractPagesFn, originalBlobUrl]);

    const onPageChanged = useCallback((startPage, endPage) => {
        setPdfPageMeta(prev => ({
            ...prev,
            startPage,
            endPage,
        }));

        // Only the extraction is debounced
        debouncedExtractPages(startPage, endPage, (newBlobUrl) => {
            setBlobUrl(newBlobUrl);
        });
    }, [debouncedExtractPages]);

    const reset = useCallback((initialStartPage=1, initialEndPage=1, pageCount=1, originalBlobUrl=null) => {
        setPdfPageMeta((prevState) => ({...prevState, ...pdfPageMetaDefaultValues, pageCount, startPage: initialStartPage, endPage: initialEndPage}));
        setBlobUrl(originalBlobUrl);
        setProcessing(false);
    }, []);

    // Initial load
    useEffect(() => {
        onPageChanged(initialStartPage, initialEndPage);

        return () => {
            debouncedExtractPages.cancel();
            // Cleanup blob URL
            if (currentBlobRef.current && currentBlobRef.current !== originalBlobUrl) {
                URL.revokeObjectURL(currentBlobRef.current);
            }
        };
    }, [initialStartPage, initialEndPage, debouncedExtractPages, onPageChanged, originalBlobUrl]);

    // Update blob when original PDF changes
    useEffect(() => {
        setBlobUrl(originalBlobUrl);
        currentBlobRef.current = originalBlobUrl;
    }, [originalBlobUrl]);

    useEffect(() => {
        return () => {
            reset();
        };
    }, [reset]);

    return {
        ...pdfPageMeta,
        reset,
        blobUrl,
        processing,
        onPageChanged,
    };
}